import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useAnalyticsCore } from "../../../../cores/analytics";
import { useFacebookCore } from "../../../../cores/facebook";
import { useInstagramCore } from "../../../../cores/instagram";
import { useMeCore } from "../../../../cores/me";
import { useTiktokCore } from "../../../../cores/tiktok";
import { useLinkedInBusinessCore } from "../../../../cores/linkedInBusiness";
import { buildSummaryAnalytics } from "./buildSummaryAnalytics";
import { fetchSummaryAnalytics } from "./fetchSummaryAnalytics";
import { getSummarySyncedPlatformList, getSummarySyncedPlatforms } from "./summarySyncedPlatforms";
import { SummaryAnalyticsSnapshot, SummaryPlatform } from "./summaryAnalyticsTypes";

export function useSummaryAnalytics(enabled: boolean) {
  const intl = useIntl();
  const { date } = useAnalyticsCore();
  const { company, socialSettings } = useMeCore();
  const { settings: facebookSettings } = useFacebookCore();
  const { settings: instagramSettings } = useInstagramCore();
  const { settings: tiktokSettings } = useTiktokCore();
  const { settings: linkedInBusinessSettings } = useLinkedInBusinessCore();

  const [snapshot, setSnapshot] = useState<SummaryAnalyticsSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingPlatforms, setPendingPlatforms] = useState<SummaryPlatform[]>([]);

  const platformSources = useMemo(
    () => ({
      socialSettings,
      facebookSettings,
      instagramSettings,
      tiktokSettings,
      linkedInBusinessSettings,
    }),
    [socialSettings, facebookSettings, instagramSettings, tiktokSettings, linkedInBusinessSettings]
  );

  const platforms = useMemo(
    () => getSummarySyncedPlatforms(platformSources),
    [platformSources]
  );

  const syncedPlatforms = useMemo(
    () => getSummarySyncedPlatformList(platformSources),
    [platformSources]
  );

  const hasAnySync = syncedPlatforms.length > 0;

  const isFutureDate = (d: string) => moment(d).isAfter(moment());
  const areDatesInverted = (since: string, until: string) =>
    moment(since).isAfter(moment(until));

  useEffect(() => {
    if (
      !enabled ||
      !company?.id ||
      !hasAnySync ||
      isFutureDate(date.since) ||
      isFutureDate(date.until) ||
      areDatesInverted(date.since, date.until)
    ) {
      setSnapshot(null);
      setLoadError(null);
      setLoading(false);
      setPendingPlatforms([]);
      return;
    }

    let cancelled = false;
    const expectedPlatforms = syncedPlatforms.filter((platform) => platforms[platform]);
    setLoading(true);
    setLoadError(null);
    setSnapshot(null);
    setPendingPlatforms(expectedPlatforms);

    fetchSummaryAnalytics(company.id, date.since, date.until, platforms, {
      onPlatformLoaded: (raw) => {
        if (cancelled) return;
        setSnapshot(buildSummaryAnalytics(raw));
      },
      onPlatformSettled: (platform) => {
        if (cancelled) return;
        setPendingPlatforms((current) => current.filter((item) => item !== platform));
      },
    })
      .then((raw) => {
        if (cancelled) return;
        const hasData = Boolean(
          raw.facebook || raw.instagram || raw.tiktok || raw.linkedinBusiness
        );
        setSnapshot(hasData ? buildSummaryAnalytics(raw) : null);
        setLoadError(null);
      })
      .catch((err: Error & { response?: { data?: { message?: string } } }) => {
        if (cancelled) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          intl.formatMessage({ id: "app.toast.error2" });
        setLoadError(typeof msg === "string" ? msg : "Error");
        setSnapshot(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setPendingPlatforms([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    company?.id,
    date.since,
    date.until,
    hasAnySync,
    platforms,
    syncedPlatforms,
    intl,
  ]);

  return {
    snapshot,
    loading,
    loadError,
    hasAnySync,
    syncedPlatforms,
    pendingPlatforms,
  };
}
